import { useTranslation } from 'react-i18next';
import { Button, Callout, Modal } from '@/components/ui';
import type { AppProvisioning } from '@/api/console';
import { AppCredentialReveal } from './AppCredentialReveal';
import { useCopyToClipboard } from './useCopyToClipboard';
import styles from './AppDetailConnectionTab.module.css';

interface AppProvisioningResultModalProps {
    /** `null` keeps the modal closed. */
    provisioning: AppProvisioning | null;
    onClose: () => void;
}

/**
 * EAA-1709: what `POST /admin/apps` / `PATCH /admin/apps/{code}` did to the app's
 * Keycloak clients, when that needs the operator: the `<code>-api` secret created
 * by THIS call (shown once, through the same `AppCredentialReveal` the
 * "Cấp lại chuỗi bí mật" flow uses) and/or the failed steps' details. The secret
 * lives only in the caller's state; closing loses it for good, so neither the
 * mask, Esc nor a close icon can dismiss it while it is shown.
 */
export function AppProvisioningResultModal({
    provisioning,
    onClose,
}: AppProvisioningResultModalProps) {
    const { t } = useTranslation();
    const copy = useCopyToClipboard();
    const credentials = provisioning?.credentials ?? null;
    const failedSteps = (provisioning?.steps ?? []).filter((step) => step.status === 'failed');
    const failed = provisioning?.status === 'failed';

    return (
        <Modal
            open={provisioning !== null}
            onCancel={onClose}
            mask={{ closable: false }}
            // With a secret on screen the only way out is "Tôi đã lưu lại".
            closable={!credentials}
            keyboard={!credentials}
            title={
                credentials
                    ? t('console.appDetail.provisioning.titleCredentials')
                    : t('console.appDetail.provisioning.titleFailed')
            }
            footer={credentials ? null : <Button onClick={onClose}>{t('common.close')}</Button>}
        >
            {failed && (
                <Callout
                    tone="danger"
                    label={t('console.appDetail.provisioning.failedLabel')}
                    longLabel
                >
                    <span data-testid="app-provisioning-failed">
                        {t('console.appDetail.provisioning.failedBody')}
                    </span>
                    {failedSteps.length > 0 && (
                        <ul className={styles.probeList}>
                            {failedSteps.map((step) => (
                                <li
                                    key={`${step.step}:${step.detail}`}
                                    className={styles.probeText}
                                >
                                    <strong className={styles.probeTitle}>{step.step}</strong>
                                    {': '}
                                    {step.detail}
                                </li>
                            ))}
                        </ul>
                    )}
                </Callout>
            )}
            {credentials && (
                <AppCredentialReveal
                    fields={[
                        {
                            label: t('console.appDetail.connection.apiClient.clientId'),
                            value: credentials.client_id,
                        },
                        {
                            label: t('console.appDetail.connection.clientSecret'),
                            value: credentials.client_secret,
                            wrap: true,
                        },
                    ]}
                    warning={t('console.appDetail.provisioning.secretWarning')}
                    rotated={false}
                    onCopy={copy}
                    onDismiss={onClose}
                />
            )}
        </Modal>
    );
}

export default AppProvisioningResultModal;
